import React, { useState, useContext, useEffect, useMemo } from "react";
import CenterContainer from "../components/CenterContainer";
import { Link, RouteComponentProps, useHistory } from "react-router-dom";
import { Test } from "consumerhouse-shared";
import AuthContext from "../AuthContext";
import {
    faArrowRight,
    faCheck,
    faTimesCircle,
    faInfoCircle,
    faCheckCircle,
    faList,
    faCheckDouble,
    faExclamationCircle,
    faMapMarkerAlt,
    faWarning,
    faUser,
} from "@fortawesome/free-solid-svg-icons";
import { QuestionAnswer, QuestionnaireFillIn } from "../components/QuestionAnswer";
import { Question } from "consumerhouse-shared";
import Alert from "../components/Alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "../components/Button";
import { calculateAge } from "consumerhouse-shared";
import { Translation, useLanguage } from "../useLanguage";
import styled from "styled-components";
import { Steps } from "../components/Steps";
import { CollapseContainer } from "../components/CollapseContainer";
import { ReactComponent as UndrawDone } from "../data/undraw_done.svg";
import { EventReserveComponent } from "./EventReserve";
import { List as LoadingList } from "react-content-loader";
import { ButtonContainer } from "../components/ButtonContainer";
import ReactMarkdown from "react-markdown";
import Spinner from "../components/Spinner";
import { TermsLink } from "../components/TermsLink";
import { NotFoundError } from "../api";

const DoneContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    font-size: 1.2em;
    /* max-width: 800px; */
`;

const UndrawWrapper = styled(UndrawDone)`
    display: flex;
    margin: 2em 0;
    width: 100%;
    /* width: 400px; */
    height: 250px;
`;

const StepsContainer = styled.div`
    padding: 3em 0 1em 0;
    background: #fff;
    /* background: #eee; */
    border-radius: 0 0 1em 1em;
`;

function ApplyQuestions(props: { accessToken?: string; testId: number; questions: Question[]; onApply: (skipReserve: boolean) => void }) {
    const [applyError, setApplyError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const { client } = useContext(AuthContext);
    const language = useLanguage();

    async function onDone(stopped: boolean) {
        setApplyError(null);
        setLoading(true);
        await new Promise((res) => setTimeout(res, 300));
        let res = await client.fetch("/test/apply", { testId: props.testId }, "POST", props.accessToken);
        if (res.status === "ok") {
            props.onApply(!res.satisfies);
        } else if (res.status === "error") {
            setApplyError(res.error!);
        }
        setLoading(false);
    }

    return (
        <div>
            <QuestionnaireFillIn accessToken={props.accessToken} questions={props.questions} onDone={onDone} loading={loading} />

            {applyError && (
                <Alert icon={faTimesCircle}>
                    <p>{language(applyError as Translation)}</p>
                </Alert>
            )}
        </div>
    );
}

function ApplyDone(props: { test: Test; onClick: () => void; hideDescription?: boolean }) {
    const history = useHistory();
    const language = useLanguage();

    useEffect(() => {
        if (props.test.redirect) {
            setTimeout(() => {
                window.location.href = props.test.redirect!;
            }, 5000);
        }
    }, []);

    return (
        <div>
            {props.test.redirect ? (
                <DoneContainer>{language("apply-redirect")}</DoneContainer>
            ) : (
                <>
                    <UndrawWrapper />
                    <DoneContainer>
                        <h3 style={{ margin: "0.8em 0" }}>{language("apply-done-title")}</h3>
                        {!props.hideDescription && <p>{language("apply-done-info")}</p>}
                        <Button color={"#666"} style={{ display: "block", margin: "1em 0" }} onClick={props.onClick} icon={faArrowRight}>
                            {props.hideDescription ? language("apply-next") : language("apply-go-account")}
                        </Button>
                    </DoneContainer>
                </>
            )}
        </div>
    );
}

function ApplyInfo(props: { test: Test; onDone: () => void; satisfies: boolean }) {
    const language = useLanguage();
    return (
        <div>
            <h1>{props.test.name}</h1>
            <ReactMarkdown>{props.test.description}</ReactMarkdown>

            <div style={{ margin: "1em 0" }}></div>
            <TermsLink />
            <ButtonContainer>
                <Button disabled={!props.satisfies} style={{ margin: "0.5em 0" }} onClick={props.onDone} icon={faArrowRight}>
                    {language("apply-next-step")}
                </Button>
            </ButtonContainer>

            {!props.satisfies && (
                <div>
                    <Alert style={{ flexWrap: "nowrap", margin: "1em 0" }} color="#e38b29" textColor="white" icon={faExclamationCircle}>
                        <div>
                            <p>{language("apply-no-satisfies-age")}</p>
                            <Link to="/">
                                <Button color="white" textColor="black" icon={faArrowRight} style={{ margin: "0.5em 0" }}>
                                    {language("app-running-tests")}
                                </Button>
                            </Link>
                        </div>
                    </Alert>
                </div>
            )}

            {/* <CollapseContainer style={{ margin: "1em 0" }} icon={faInfoCircle} title={language("apply-steps-title")}>
                <ol>
                    <li>{language("apply-step1")}</li>
                    <li>{language("apply-step2")}</li>
                    <li>{language("apply-step3")}</li>
                    <li>{language("apply-step4")}</li>
                    <li>{language("apply-step5")}</li>
                </ol>
            </CollapseContainer> */}
        </div>
    );
}

export default function Apply({ match }: RouteComponentProps<{ id: string }>) {
    const { client, auth } = useContext(AuthContext);
    const [test, setTest] = useState<Test | null>(null);
    const [loadingTest, setLoadingTest] = useState(false);
    const history = useHistory();
    const [step, setStep] = useState<"start" | "questions" | "reserve" | "done">("start");
    const userAge = useMemo(() => (auth ? calculateAge(new Date(auth!.user.birthTimeStamp)) : 50), [auth]);
    const language = useLanguage();
    const testId = parseInt(match.params.id);
    const allQuestions = test?.questionnaires?.reduce((p, e) => p.concat(e.questions!), [] as Question[]);
    const [accessToken, redirect] = useMemo(() => {
        let params = new URLSearchParams(window.location.search);
        return [params.get("accessToken") || undefined, params.get("redirect") || undefined];
    }, [window.location.search]);

    useEffect(() => {
        (async () => {
            setLoadingTest(true);
            try {
                let res = await client.fetch("/test", { testId, registrationQuestionsOnly: true }, "POST", accessToken);
                if (!res) throw new NotFoundError("res == null");
                setTest(res.test);
            } catch (ex) {
                if (ex instanceof NotFoundError) {
                    console.warn("Test not found", ex);
                } else {
                    throw ex;
                }
            } finally {
                setLoadingTest(false);
            }
        })();
    }, [client, history, match.params.id, testId]);

    useEffect(() => {
        if (step === "done") window.scrollTo(0, 144);
        else window.scrollTo(0, 0);
    }, [step]);

    return (
        <>
            <CenterContainer width="800px" style={{ margin: "0 0 5em 0" }}>
                <StepsContainer>
                    <Steps
                        currentIndex={["start", "questions", "reserve", "done"].indexOf(step)}
                        doneIcon={faCheckCircle}
                        steps={[
                            { name: language("apply-step-info"), icon: faInfoCircle },
                            {
                                name: language("apply-step-questions"),
                                icon: faList,
                                hidden: !test || allQuestions!.length <= 0,
                            },
                            {
                                name: language("apply-step-reserve"),
                                icon: faMapMarkerAlt,
                                hidden: !test || test.events!.length <= 0,
                            },
                            { name: language("apply-step-done"), icon: faCheck },
                        ]}
                    />
                </StepsContainer>
                {test ? (
                    <>
                        {step === "start" && (
                            <ApplyInfo
                                satisfies={(userAge >= test.minAge && userAge <= test.maxAge) || !!auth?.user.admin}
                                onDone={async () => {
                                    if (allQuestions!.length > 0) {
                                        setStep("questions");
                                    } else {
                                        let res = await client.fetch("/test/apply", { testId }, "POST", accessToken);
                                        if (res.status === "ok") {
                                            if (res.satisfies && test.events!.length > 0) setStep("reserve");
                                            else setStep("done");
                                        } else {
                                            console.warn("could not register", res);
                                        }
                                    }
                                }}
                                test={test}
                            />
                        )}
                        {step === "questions" && (
                            <ApplyQuestions
                                accessToken={accessToken}
                                testId={testId}
                                questions={allQuestions!}
                                onApply={(skipReserve) => {
                                    if (!skipReserve && test.events!.length > 0) setStep("reserve");
                                    else setStep("done");
                                }}
                            />
                        )}
                        {step === "reserve" && (
                            <EventReserveComponent
                                event={test.events![0]}
                                onApply={() => {
                                    setStep("done");
                                }}
                            />
                        )}
                        {step === "done" && (
                            <ApplyDone
                                hideDescription={!!redirect}
                                test={test}
                                onClick={() => {
                                    if (redirect && redirect.startsWith("/")) {
                                        history.push(redirect);
                                    } else {
                                        history.push("/account");
                                    }
                                }}
                            />
                        )}
                    </>
                ) : loadingTest ? (
                    <LoadingList />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Alert color="#e38b29" textColor="white" icon={faExclamationCircle}>
                            <p>{language("apply-test-closed")}</p>
                        </Alert>
                        <Button style={{ marginTop: "1rem" }} icon={faArrowRight} onClick={() => history.push("/")}>
                            {language("nav-menu-tests")}
                        </Button>
                    </div>
                )}
            </CenterContainer>
        </>
    );
}
